/**
 * Subtle synthesized sound (WebAudio, no assets): radar contact pings, the inbound alarm,
 * distant/near detonations, intercept ticks and UI clicks. Muted state persists.
 */
const MUTE_KEY = 'sa.muted';

function loadMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  muted = loadMuted();
  /** the context came up or SOUND was toggled (the music follows) */
  onChange: (() => void) | null = null;

  /** The shared AudioContext, once a user gesture unlocked it. */
  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Must be called from a user gesture at least once (autoplay policy). */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 0.5;
    this.master.connect(this.ctx.destination);
    this.onChange?.();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    try {
      localStorage.setItem(MUTE_KEY, this.muted ? '1' : '0');
    } catch {
      /* storage blocked: session-only */
    }
    if (this.master) this.master.gain.value = this.muted ? 0 : 0.5;
    this.onChange?.();
    return this.muted;
  }

  private tone(freq: number, dur: number, vol: number, type: OscillatorType = 'sine', at = 0, slideTo?: number): void {
    const c = this.ctx;
    if (!c || !this.master || this.muted) return;
    const t = c.currentTime + at;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private noise(dur: number, vol: number, cutoff: number): void {
    const c = this.ctx;
    if (!c || !this.master || this.muted) return;
    const len = Math.floor(c.sampleRate * dur);
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    let seed = 0x1234567;
    for (let i = 0; i < len; i++) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      d[i] = ((seed / 0x7fffffff) * 2 - 1) * Math.pow(1 - i / len, 2.2);
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = cutoff;
    const g = c.createGain();
    g.gain.value = vol;
    src.connect(f).connect(g).connect(this.master);
    src.start();
  }

  /**
   * A toast comes up: a short teletype chirp in its tone — two soft blips (news), rising (an ally),
   * a lower double (warning), a falling square (danger), three quick blips (an offer to answer) and
   * a low swell under a falling pair (a big moment).
   */
  toast(tone: 'info' | 'ok' | 'ally' | 'warn' | 'danger', kind: 'news' | 'offer' | 'moment' | 'chat' = 'news'): void {
    if (kind === 'offer') {
      for (let i = 0; i < 3; i++) this.tone(1480, 0.05, 0.035, 'square', i * 0.09);
      return;
    }
    if (kind === 'moment') {
      this.tone(110, 0.9, 0.07, 'sawtooth', 0, 70);
      this.tone(880, 0.18, 0.06, 'square', 0.05, 520);
      this.tone(660, 0.22, 0.05, 'square', 0.28, 330);
      return;
    }
    switch (tone) {
      case 'danger':
        this.tone(520, 0.16, 0.05, 'square', 0, 300);
        break;
      case 'warn':
        this.tone(700, 0.07, 0.04, 'square');
        this.tone(700, 0.07, 0.04, 'square', 0.11);
        break;
      case 'ally':
        this.tone(990, 0.08, 0.035, 'sine');
        this.tone(1320, 0.1, 0.035, 'sine', 0.08);
        break;
      default:
        this.tone(1760, 0.03, 0.025, 'square');
        this.tone(1760, 0.03, 0.025, 'square', 0.06);
    }
  }

  ping(): void {
    this.tone(1320, 0.35, 0.05, 'sine');
  }

  inbound(): void {
    for (let i = 0; i < 3; i++) {
      this.tone(880, 0.12, 0.09, 'square', i * 0.28);
      this.tone(660, 0.12, 0.07, 'square', i * 0.28 + 0.14);
    }
  }

  tick(): void {
    this.tone(1760, 0.05, 0.03, 'square');
  }

  hint(): void {
    this.tone(220, 0.4, 0.06, 'triangle', 0, 180);
  }

  launch(): void {
    this.tone(140, 0.9, 0.12, 'sawtooth', 0, 50);
    this.noise(0.8, 0.08, 900);
  }

  detonation(near: boolean): void {
    this.noise(near ? 2.2 : 1.2, near ? 0.5 : 0.12, near ? 700 : 300);
    this.tone(near ? 55 : 45, near ? 1.6 : 0.9, near ? 0.25 : 0.06, 'sine', 0, 30);
  }

  intercept(hit: boolean): void {
    this.tone(hit ? 2400 : 900, 0.18, 0.05, 'triangle', 0, hit ? 1200 : 700);
  }

  click(): void {
    this.tone(2000, 0.03, 0.025, 'square');
  }

  error(): void {
    this.tone(180, 0.12, 0.05, 'square');
  }
}
